import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import type { PopoverPosition, SxProps, Theme } from "@mui/material";
import { Box, Button, Collapse, Paper, Stack, Typography, useTheme } from "@mui/material";
import FocusTrap from "@mui/material/Unstable_TrapFocus";
import type { RefObject } from "react";
import { useCallback, useEffect, useEffectEvent, useRef, useState } from "react";
import { Outlet, useLocation, useNavigate, useOutlet, useParams } from "react-router";
import { withErrorHandling } from "@entrilia/shared/api/axiosHelper";
import type { ApiError } from "@entrilia/shared/api/types";
import type { ArtifactPickedPart, ArtifactThemeMode } from "@entrilia/shared/artifactRuntime/protocol";
import DataLoadingFailed from "@entrilia/shared/components/DataLoadingFailed";
import NoAccessPage from "@entrilia/shared/components/NoAccessPage";
import StatusChip from "@entrilia/shared/components/StatusChip";
import InlineLoader from "@entrilia/shared/components/inlineLoader/InlineLoader";
import { useNotificationContext } from "@entrilia/shared/contexts/NotificationContext";
import useFetch from "@entrilia/shared/hooks/useFetch";
import usePageTitle from "@entrilia/shared/hooks/usePageTitle";
import { logError } from "@entrilia/shared/logging";
import type { Validator } from "@entrilia/shared/utilities/validators";
import { combineValidators, maxCharactersValidator, requiredValidator } from "@entrilia/shared/utilities/validators";
import biApi from "../../../../api/biApi";
import type { SmartViewArtifact, SmartViewArtifactVersion } from "../../../../api/types/smartViewArtifactsTypes";
import type { ReportAuthorizationInfo } from "../../../../api/types/svTypes";
import useClientContext from "../../../../context/useClientContext";
import EditableTitle from "../../../common/EditableTitle";
import GeneralPageHeader from "../../../common/GeneralPageHeader";
import DraftStatus from "../report-lists/templates/status/DraftStatus";
import ArtifactSelector from "./ArtifactSelector";
import ArtifactShareButton from "./ArtifactShareButton";
import ArtifactSplitPane from "./ArtifactSplitPane";
import ArtifactToolbar from "./ArtifactToolbar";
import { getArtifactPageBackground } from "./artifactPageBackground";
import ArtifactAgentCollapsedHeader from "./agent/ArtifactAgentCollapsedHeader";
import ArtifactAgentHeader from "./agent/ArtifactAgentHeader";
import type { ArtifactAgentPanelHandle } from "./agent/ArtifactAgentPanel";
import { describeBuilderView } from "./agent/builderView";
import { toPickedPart } from "./agent/pickedParts";
import ArtifactAgentPanel from "./agent/ArtifactAgentPanel";
import type { ArtifactSourceStore } from "./canvas/artifactSourceStore";
import { createArtifactSourceStore } from "./canvas/artifactSourceStore";
import ArtifactCheckerFrame from "./canvas/ArtifactCheckerFrame";
import type { ArtifactCheckResult, ArtifactRuntimeError } from "./canvas/checkResult";
import ArtifactDataSources from "./canvas/ArtifactDataSources";
import ArtifactFigureMenu from "./canvas/ArtifactFigureMenu";
import type { ArtifactFrameHandle } from "./canvas/ArtifactFrame";
import ArtifactFrame from "./canvas/ArtifactFrame";
import type { ArtifactLedgerDrillDownHandle } from "./canvas/ArtifactLedgerDrillDown";
import ArtifactLedgerDrillDown from "./canvas/ArtifactLedgerDrillDown";
import type { SourceFigureTarget, SourceTarget } from "./canvas/sourceRequestsState";
import useArtifactLogo from "./canvas/useArtifactLogo";
import ArtifactFilterBar from "./filters/ArtifactFilterBar";
import {
  addFilterLabelsAction,
  applyDefaultsAction,
  buildFilterLabels,
  clearFilterAction,
  findFiltersNeedingLabels,
  initialArtifactFilterState,
  setFilterAction,
} from "./filters/filterState";
import { getArtifactPreviewKey } from "./previews/artifactPreviewCache";
import type { SourceReportRequest } from "./report/sourceReportRequest";
import useCanManageArtifacts from "./useCanManageArtifacts";
import VersionMenu from "./versions/VersionMenu";

const expandedAgentPanelSx: SxProps<Theme> = {
  flex: 1,
  minWidth: 0,
  "& .MuiCollapse-wrapper, & .MuiCollapse-wrapperInner": { width: "100%" },
};

const slidingAgentPanelSx: SxProps<Theme> = { flex: "0 0 auto" };

const MAX_NAME_LENGTH = 60;

const trimmedNameValidator = combineValidators(
  requiredValidator,
  maxCharactersValidator(MAX_NAME_LENGTH, `Dashboard names can be up to ${MAX_NAME_LENGTH} characters`)
);

const nameValidator: Validator<string> = (name) => trimmedNameValidator(name.trim());

const renameArtifact = withErrorHandling(biApi.smartViewArtifacts.rename);

const saveArtifactPreview = withErrorHandling(biApi.smartViewArtifacts.savePreview);

const isChatFocusRequested = (locationState: unknown): boolean =>
  typeof locationState === "object" &&
  locationState !== null &&
  "focusChat" in locationState &&
  locationState.focusChat === true;

interface ArtifactTitleProps {
  artifactId: string;
  artifactName: string;
  title: string;
  mode: "view" | "edit";
  publishedVersion?: number;
  canRename: boolean;
  onRenamed: (name: string) => void;
}

const ArtifactTitle = ({
  artifactId,
  artifactName,
  title,
  mode,
  publishedVersion,
  canRename,
  onRenamed,
}: ArtifactTitleProps) => {
  const { sendNotification, sendNotificationError } = useNotificationContext();

  const handleTitleChanged = async (name: string) => {
    if (name === title) {
      return;
    }
    const [renamed, error] = await renameArtifact(artifactId, name);
    if (error) {
      logError(error, "[ArtifactTitle] rename");
      sendNotificationError(error.message);
      throw error;
    }
    onRenamed(renamed.name);
    sendNotification("The dashboard was renamed.");
  };

  return (
    <EditableTitle
      title={title}
      TitleComponent={
        <ArtifactSelector artifactId={artifactId} artifactName={artifactName} title={title} mode={mode} />
      }
      tooltip="Rename Dashboard"
      editingDisabled={!canRename}
      placeholder="Enter Dashboard Name"
      validator={nameValidator}
      onTitleChanged={handleTitleChanged}
    >
      {publishedVersion ? (
        <StatusChip label={`Published v${publishedVersion}`} withDot color={(t) => t.palette.success.main} />
      ) : (
        <DraftStatus />
      )}
    </EditableTitle>
  );
};

interface FigureMenuState {
  version: number;
  target: SourceFigureTarget;
  anchorPosition: PopoverPosition;
}

interface HeldFrameError {
  version: number;
  error: ArtifactRuntimeError;
}

interface ArtifactCanvasProps {
  frameRef: RefObject<ArtifactFrameHandle | null>;
  sourceStore: ArtifactSourceStore;
  artifactId: string;
  viewedVersion: number;
  version: SmartViewArtifactVersion | undefined;
  versionError: ApiError | undefined;
  filterValues: Record<string, string[]>;
  filterLabels: Record<string, Record<string, string>>;
  mode: ArtifactThemeMode;
  isViewer: boolean;
  isChecking: boolean;
  drillMode: boolean;
  pickMode: boolean;
  onSetFilter: (key: string, values: string[]) => void;
  onClearFilter: (key: string) => void;
  getCompareMode: (version: number) => ArtifactThemeMode | undefined;
  onRuntimeError: (version: number, error: ArtifactRuntimeError) => void;
  onCheckFinished: (result: ArtifactCheckResult) => void;
  onFigureClick: (version: number, target: SourceFigureTarget, anchorPosition: PopoverPosition) => void;
  onCheckPreviewCaptured: (artifactId: string, version: number, image: Blob) => void;
  onFramePreviewCaptured?: (artifactId: string, version: number, image: Blob) => void;
  onPartPicked: (version: number, part: ArtifactPickedPart, keepPicking: boolean) => void;
  onPickCancelled: () => void;
}

const ArtifactCanvas = ({
  frameRef,
  sourceStore,
  artifactId,
  viewedVersion,
  version,
  versionError,
  filterValues,
  filterLabels,
  mode,
  isViewer,
  isChecking,
  drillMode,
  pickMode,
  getCompareMode,
  onSetFilter,
  onClearFilter,
  onRuntimeError,
  onCheckFinished,
  onFigureClick,
  onCheckPreviewCaptured,
  onFramePreviewCaptured,
  onPartPicked,
  onPickCancelled,
}: ArtifactCanvasProps) => {
  const { loading: isLogoLoading, logo } = useArtifactLogo(artifactId, version);

  if (versionError) {
    logError(versionError, "[ArtifactPage] getVersion");
    return <DataLoadingFailed title="Failed to load the dashboard" text={versionError.message} />;
  }
  if (viewedVersion === 0) {
    return (
      <Typography color="textSecondary" sx={{ p: 3 }}>
        This dashboard has no versions yet.
      </Typography>
    );
  }
  if (version?.version !== viewedVersion || isLogoLoading) {
    return <InlineLoader />;
  }
  return (
    <Stack sx={{ flex: 1, minHeight: 0, position: "relative" }}>
      <ArtifactFrame
        key={`${artifactId}-${version.version}`}
        ref={frameRef}
        sourceStore={sourceStore}
        artifactId={artifactId}
        version={version}
        filterValues={filterValues}
        filterLabels={filterLabels}
        mode={mode}
        logo={logo}
        isViewer={isViewer}
        drillMode={drillMode}
        pickMode={pickMode}
        onSetFilter={onSetFilter}
        onClearFilter={onClearFilter}
        onRuntimeError={onRuntimeError}
        onFigureClick={onFigureClick}
        onPreviewCaptured={onFramePreviewCaptured}
        onPartPicked={onPartPicked}
        onPickCancelled={onPickCancelled}
      />
      {isChecking && (
        <ArtifactCheckerFrame
          key={`check-${version.version}`}
          sourceStore={sourceStore}
          artifactId={artifactId}
          version={version}
          filterValues={filterValues}
          filterLabels={filterLabels}
          mode={mode}
          logo={logo}
          getCompareMode={getCompareMode}
          onFinished={onCheckFinished}
          onPreviewCaptured={onCheckPreviewCaptured}
        />
      )}
    </Stack>
  );
};

interface Props {
  mode: "view" | "edit";
}

interface ArtifactPageContentProps {
  artifactId: string;
  mode: "view" | "edit";
}

const recordOpened = withErrorHandling(biApi.smartViewArtifacts.recordOpened);

const recordArtifactOpened = async (artifactId: string) => {
  const [, error] = await recordOpened(artifactId);
  if (error && !error.isCanceledRequest) {
    logError(error, "[ArtifactPage] recordOpened");
  }
};

const useEscapeKey = (isActive: boolean, onEscape: () => void): void => {
  const handleEscape = useEffectEvent(onEscape);
  useEffect(() => {
    if (!isActive) {
      return;
    }
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        handleEscape();
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [isActive]);
};

const ArtifactPageContent = ({ artifactId, mode }: ArtifactPageContentProps) => {
  const location = useLocation();
  const navigate = useNavigate();
  const outlet = useOutlet();
  const theme = useTheme();
  const { hasPermissions } = useClientContext();
  const canManage = useCanManageArtifacts();
  const isEditing = mode === "edit" && canManage;
  const isEditDenied = mode === "edit" && !canManage;
  const [filterState, setFilterState] = useState(initialArtifactFilterState);
  const [selectedVersion, setSelectedVersion] = useState<number>();
  const [chatResetCount, setChatResetCount] = useState(0);
  const [isChatTurnRunning, setIsChatTurnRunning] = useState(false);
  const [checkVersion, setCheckVersion] = useState<number>();
  const [isDrillMode, setIsDrillMode] = useState(false);
  const [isFullScreen, setIsFullScreen] = useState(false);
  const [isPickMode, setIsPickMode] = useState(false);
  const [figureMenu, setFigureMenu] = useState<FigureMenuState>();
  const [sourceStore] = useState(createArtifactSourceStore);
  const [isAgentPanelCollapsed, setIsAgentPanelCollapsed] = useState(false);
  const [agentPanelWidth, setAgentPanelWidth] = useState<number>();
  const [hasUnseenAgentUpdate, setHasUnseenAgentUpdate] = useState(false);
  const [capturedPreviewKeys, setCapturedPreviewKeys] = useState<ReadonlySet<string>>(() => new Set());
  const frameRef = useRef<ArtifactFrameHandle>(null);
  const agentPanelRef = useRef<ArtifactAgentPanelHandle>(null);
  const heldFrameErrorsRef = useRef<HeldFrameError[]>([]);
  const agentPanelBoxRef = useRef<HTMLDivElement>(null);
  const ledgerRef = useRef<ArtifactLedgerDrillDownHandle>(null);

  const getArtifact = useCallback(
    (signal?: AbortSignal) => biApi.smartViewArtifacts.get(artifactId, signal),
    [artifactId]
  );
  const [artifact, artifactError, { fetch: refetchArtifact, setData: setArtifact }] = useFetch(getArtifact);
  const shownArtifactId = isEditDenied ? undefined : artifact?.id;
  useEffect(() => {
    if (shownArtifactId) {
      recordArtifactOpened(shownArtifactId);
    }
  }, [shownArtifactId]);
  const latestVersion = artifact?.latestVersion ?? 0;
  const viewedVersion = isEditing ? (selectedVersion ?? latestVersion) : (artifact?.publishedVersion ?? latestVersion);

  const getVersion = useCallback(
    (signal?: AbortSignal) => biApi.smartViewArtifacts.getVersion(artifactId, viewedVersion, signal),
    [artifactId, viewedVersion]
  );
  const [version, versionError] = useFetch(getVersion, {
    disabled: viewedVersion === 0,
    onSetData: (data) => setFilterState(applyDefaultsAction(data)),
  });

  const getVersions = useCallback(
    (signal?: AbortSignal) => biApi.smartViewArtifacts.listVersions(artifactId, signal),
    [artifactId]
  );
  const [versions, versionsError, { fetch: refetchVersions }] = useFetch(getVersions, { disabled: !isEditing });

  const viewedVersionTitle = !isEditing && version?.version === viewedVersion ? version.title : undefined;
  const shownTitle = viewedVersionTitle || artifact?.name;
  const isLatestVersionShown = version?.version === latestVersion && version.version === viewedVersion;
  usePageTitle(shownTitle ?? "Dashboard");

  useEffect(() => {
    const pending = findFiltersNeedingLabels(filterState);
    if (pending.length === 0) {
      return;
    }
    const controller = new AbortController();
    const searchDictionary = withErrorHandling(biApi.searchDimensionDictionary);

    const lookupLabels = async () => {
      const results = await Promise.all(
        pending.map(async (request) => {
          const [result, error] = await searchDictionary(
            request.dataSetId,
            {
              dimensionName: request.dimensionName,
              selectedValuesUserInput: request.values,
              allRows: true,
              range: { skip: 0, take: 0, totals: false },
            },
            controller.signal
          );
          return { request, result, error };
        })
      );
      if (controller.signal.aborted) {
        return;
      }
      for (const { request, result, error } of results) {
        if (error) {
          if (!error.isCanceledRequest) {
            logError(error, "[ArtifactPage] searchDimensionDictionary");
          }
          continue;
        }
        if (result) {
          const labels = buildFilterLabels(request.values, result.keyFieldName, result.items);
          setFilterState(addFilterLabelsAction(request.key, labels));
        }
      }
    };

    lookupLabels();
    return () => controller.abort();
  }, [filterState]);

  useEscapeKey(isFullScreen, () => setIsFullScreen(false));
  useEscapeKey(isPickMode, () => setIsPickMode(false));

  const handleSetFilter = (key: string, values: string[]) => setFilterState(setFilterAction(key, values));

  const handleClearFilter = (key: string) => setFilterState(clearFilterAction(key));

  const handlePickFilterValues = (key: string, values: string[], labels: Record<string, string>) => {
    setFilterState(setFilterAction(key, values));
    setFilterState(addFilterLabelsAction(key, labels));
  };

  const handleRefreshClick = () => frameRef.current?.refresh();

  const showVersion = (nextVersion: number | undefined) => {
    setSelectedVersion(nextVersion);
    setFigureMenu(undefined);
    setIsPickMode(false);
  };

  const handleArtifactChanged = async () => {
    await Promise.all([refetchArtifact(), refetchVersions()]);
    showVersion(undefined);
  };

  const handleVersionSaved = async (savedVersion: number) => {
    await handleArtifactChanged();
    // Started after the refetch so its errors arrive once the chat turn has finished and can trigger the automatic fix.
    setCheckVersion(savedVersion);
  };

  const handleRenamed = (name: string) => setArtifact((current) => current && { ...current, name });

  const handleSharingUpdated = (authorization: ReportAuthorizationInfo, inheritFromParent: boolean) =>
    setArtifact((current) => current && { ...current, authorization, inheritFromParent });

  const handleVersionPublished = (published: SmartViewArtifact) => {
    setArtifact(published);
    refetchVersions();
  };

  const handleVersionRestored = async () => {
    setCheckVersion(undefined);
    await handleArtifactChanged();
    setChatResetCount((count) => count + 1);
  };

  const handleEditClick = () => navigate("../edit");

  const handleFullScreenChange = (isFullScreenNext: boolean) => {
    setIsFullScreen(isFullScreenNext);
    setIsPickMode(false);
  };

  const handleExpandCollapseClick = () => {
    setIsPickMode(false);
    if (!isAgentPanelCollapsed) {
      setAgentPanelWidth(agentPanelBoxRef.current?.offsetWidth);
    }
    setIsAgentPanelCollapsed(!isAgentPanelCollapsed);
    setHasUnseenAgentUpdate(false);
  };

  const handleAgentPanelEntered = () => setAgentPanelWidth(undefined);

  const handleTurnRunningChange = (isRunning: boolean) => {
    setIsChatTurnRunning(isRunning);
    if (isRunning) {
      setIsPickMode(false);
    }
    if (!isRunning) {
      setHasUnseenAgentUpdate(true);
    }
  };

  const handleRuntimeError = (errorVersion: number, error: ArtifactRuntimeError) => {
    if (errorVersion === checkVersion) {
      heldFrameErrorsRef.current.push({ version: errorVersion, error });
      return;
    }
    if (agentPanelRef.current?.reportRuntimeError(errorVersion, error)) {
      setHasUnseenAgentUpdate(true);
    }
  };

  const handlePartPicked = (pickedVersion: number, part: ArtifactPickedPart, keepPicking: boolean) => {
    const isCurrent = isLatestVersionShown && version.version === pickedVersion;
    const hasRoom = isCurrent && agentPanelRef.current?.addPickedPart(toPickedPart(version, part)) === true;
    if (!keepPicking || !hasRoom) {
      setIsPickMode(false);
    }
  };

  const handlePickCancelled = () => setIsPickMode(false);

  const getBuilderView = (): string | undefined => {
    const screen = frameRef.current?.getScreen();
    return isLatestVersionShown && screen
      ? describeBuilderView(version, screen, filterState.values, filterState.labels)
      : undefined;
  };

  const handleCheckFinished = (result: ArtifactCheckResult) => {
    setCheckVersion((current) => (current === result.version ? undefined : current));
    const heldErrors = heldFrameErrorsRef.current.filter(({ version }) => version === result.version);
    heldFrameErrorsRef.current = [];
    const errors = [...result.errors, ...heldErrors.map(({ error }) => error)];
    if (agentPanelRef.current?.addCheckResult({ ...result, errors })) {
      setHasUnseenAgentUpdate(true);
    }
  };

  const handlePreviewCaptured = async (capturedArtifactId: string, capturedVersion: number, image: Blob) => {
    const key = getArtifactPreviewKey(capturedArtifactId, capturedVersion);
    setCapturedPreviewKeys((keys) => new Set(keys).add(key));
    const [, error] = await saveArtifactPreview(capturedArtifactId, capturedVersion, image);
    if (error) {
      if (!error.isCanceledRequest) {
        logError(error, "[ArtifactPage] savePreview");
      }
      setCapturedPreviewKeys((keys) => new Set([...keys].filter((capturedKey) => capturedKey !== key)));
    }
  };

  const handleDrillModeChange = (enabled: boolean) => {
    setIsDrillMode(enabled);
    setFigureMenu(undefined);
  };

  const handleFigureClick = (figureVersion: number, target: SourceFigureTarget, anchorPosition: PopoverPosition) =>
    setFigureMenu({ version: figureVersion, target, anchorPosition });

  const handleOpenReport = (target: SourceTarget) => {
    if (!version) {
      return;
    }
    const request: SourceReportRequest = {
      version: version.version,
      sourceKey: target.sourceKey,
      filterValues: target.filterValues,
      overrides: target.overrides,
    };
    navigate(hasPermissions(["ManageReports"]) ? "report/edit" : "report/view", { state: request });
  };

  const handleShowLedgerEntries = (target: SourceFigureTarget) => {
    const source = version?.sources.find((candidate) => candidate.key === target.sourceKey);
    const column = source?.columns.find((candidate) => candidate.key === target.columnKey);
    if (!version || !source || !column || !target.drillDown) {
      return;
    }
    ledgerRef.current?.open({
      version: version.version,
      sourceKey: source.key,
      filterValues: target.filterValues,
      overrides: target.overrides,
      reportId: source.reportId,
      fieldGuid: column.fieldGuid,
      value: target.value,
      sessionId: target.drillDown.sessionId,
      etag: target.drillDown.etag,
      drillDownId: target.drillDown.drillDownId,
    });
  };

  if (isEditDenied) {
    return <NoAccessPage />;
  }

  if (artifactError) {
    if (artifactError.code === 403) {
      return <NoAccessPage />;
    }
    logError(artifactError, "[ArtifactPage] getArtifact");
    return <DataLoadingFailed title="Failed to load the dashboard" text={artifactError.message} />;
  }

  if (!artifact) {
    return <InlineLoader />;
  }

  const isChecking =
    isEditing &&
    checkVersion !== undefined &&
    version?.version === checkVersion &&
    filterState.artifactId === artifact.id &&
    filterState.version === checkVersion;
  const shouldCaptureMissingPreview =
    canManage &&
    theme.palette.mode === "light" &&
    !isDrillMode &&
    !isChecking &&
    !artifact.pictureVersions.includes(viewedVersion) &&
    !capturedPreviewKeys.has(getArtifactPreviewKey(artifact.id, viewedVersion));

  const isVersionShown = version !== undefined && version.version === viewedVersion;
  const canPick = isEditing && isLatestVersionShown && !isFullScreen && !isAgentPanelCollapsed && !isChatTurnRunning;
  const isPickModeActive = isPickMode && canPick;
  const isReportWindowOpen = outlet !== null;
  const shownFigureMenu = isVersionShown && figureMenu?.version === version.version ? figureMenu : undefined;

  const dashboard = (
    <FocusTrap open={isFullScreen} disableEnforceFocus disableRestoreFocus>
      <Paper
        variant="outlined"
        tabIndex={isFullScreen ? -1 : undefined}
        role={isFullScreen ? "dialog" : undefined}
        aria-modal={isFullScreen || undefined}
        aria-label={isFullScreen ? (shownTitle ?? artifact.name) : undefined}
        sx={{
          display: "flex",
          flexDirection: "column",
          flex: 1,
          minHeight: 0,
          overflow: "hidden",
          // Moving the iframe into a dialog would reload the dashboard.
          ...(isFullScreen && {
            position: "fixed",
            inset: 0,
            zIndex: (t: Theme) => t.zIndex.drawer + 1,
            border: 0,
            borderRadius: 0,
          }),
        }}
      >
        {viewedVersion > 0 && (
          <ArtifactToolbar
            isDrillMode={isDrillMode}
            isFullScreen={isFullScreen}
            dataSources={
              isVersionShown && (
                <ArtifactDataSources
                  sources={version.sources}
                  filterLabels={filterState.labels}
                  getTargets={() => frameRef.current?.listActiveTargets() ?? []}
                  onOpenReport={handleOpenReport}
                />
              )
            }
            onDrillModeChange={handleDrillModeChange}
            onFullScreenChange={handleFullScreenChange}
            onRefresh={handleRefreshClick}
          >
            {version && (
              <ArtifactFilterBar
                filters={filterState.filters}
                values={filterState.values}
                labels={filterState.labels}
                onChange={handlePickFilterValues}
                onClear={handleClearFilter}
              />
            )}
          </ArtifactToolbar>
        )}
        <ArtifactCanvas
          frameRef={frameRef}
          sourceStore={sourceStore}
          artifactId={artifact.id}
          viewedVersion={viewedVersion}
          version={version}
          versionError={versionError}
          filterValues={filterState.values}
          filterLabels={filterState.labels}
          mode={theme.palette.mode}
          isViewer={!isEditing}
          isChecking={isChecking}
          drillMode={isDrillMode}
          pickMode={isPickModeActive}
          getCompareMode={(checkedVersion) => agentPanelRef.current?.getCompareMode(checkedVersion)}
          onSetFilter={handleSetFilter}
          onClearFilter={handleClearFilter}
          onRuntimeError={handleRuntimeError}
          onCheckFinished={handleCheckFinished}
          onFigureClick={handleFigureClick}
          onCheckPreviewCaptured={handlePreviewCaptured}
          onFramePreviewCaptured={shouldCaptureMissingPreview ? handlePreviewCaptured : undefined}
          onPartPicked={handlePartPicked}
          onPickCancelled={handlePickCancelled}
        />
      </Paper>
    </FocusTrap>
  );

  return (
    <Stack
      sx={{
        flex: 1,
        minHeight: 0,
        bgcolor: getArtifactPageBackground,
      }}
    >
      <GeneralPageHeader
        hideBorderLine
        TitleComponent={
          <ArtifactTitle
            artifactId={artifact.id}
            artifactName={artifact.name}
            title={shownTitle ?? artifact.name}
            mode={mode}
            publishedVersion={artifact.publishedVersion}
            canRename={isEditing}
            onRenamed={handleRenamed}
          />
        }
        showDefaultBackButtonTo="../.."
      >
        {canManage && (
          <ArtifactShareButton
            artifact={artifact}
            name={shownTitle ?? artifact.name}
            onSharingUpdated={handleSharingUpdated}
          />
        )}
        {isEditing && viewedVersion > 0 && (
          <VersionMenu
            artifactId={artifact.id}
            versions={versions}
            versionsError={versionsError}
            viewedVersion={viewedVersion}
            latestVersion={latestVersion}
            publishedVersion={artifact.publishedVersion}
            actionsDisabled={isChatTurnRunning || version?.version !== viewedVersion}
            onSelect={showVersion}
            onPublished={handleVersionPublished}
            onRestored={handleVersionRestored}
          />
        )}
        {!isEditing && canManage && (
          <Button variant="outlined" color="secondary" startIcon={<EditOutlinedIcon />} onClick={handleEditClick}>
            Edit
          </Button>
        )}
      </GeneralPageHeader>
      <Box sx={{ display: "flex", flex: 1, minHeight: 0, px: 3, pb: 1.5 }}>
        {isEditing ? (
          <ArtifactSplitPane
            isRightPaneCollapsed={agentPanelWidth !== undefined}
            leftPane={dashboard}
            rightPane={
              <Box inert={isFullScreen} sx={{ display: "flex", flex: 1, minHeight: 0 }}>
                <Collapse orientation="horizontal" in={isAgentPanelCollapsed}>
                  <ArtifactAgentCollapsedHeader
                    isRunning={isChatTurnRunning}
                    hasUnseenUpdate={hasUnseenAgentUpdate}
                    onExpand={handleExpandCollapseClick}
                  />
                </Collapse>
                <Collapse
                  orientation="horizontal"
                  in={!isAgentPanelCollapsed}
                  sx={agentPanelWidth === undefined ? expandedAgentPanelSx : slidingAgentPanelSx}
                  onEntered={handleAgentPanelEntered}
                >
                  <Paper
                    ref={agentPanelBoxRef}
                    variant="outlined"
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      width: agentPanelWidth ?? "100%",
                      height: "100%",
                      overflow: "hidden",
                    }}
                  >
                    <ArtifactAgentHeader onCollapse={handleExpandCollapseClick} />
                    <ArtifactAgentPanel
                      key={`${artifact.id}-${chatResetCount}`}
                      ref={agentPanelRef}
                      artifactId={artifact.id}
                      latestVersion={latestVersion}
                      viewedVersion={viewedVersion}
                      versions={versions}
                      versionsError={versionsError}
                      focusInput={isChatFocusRequested(location.state)}
                      isCollapsed={agentPanelWidth !== undefined}
                      onVersionSaved={handleVersionSaved}
                      onArtifactChanged={handleArtifactChanged}
                      onTurnRunningChange={handleTurnRunningChange}
                      isPickMode={isPickModeActive}
                      canPick={canPick}
                      getBuilderView={getBuilderView}
                      onPickModeChange={setIsPickMode}
                    />
                  </Paper>
                </Collapse>
              </Box>
            }
          />
        ) : (
          dashboard
        )}
      </Box>
      {shownFigureMenu && isVersionShown && (
        <ArtifactFigureMenu
          anchorPosition={shownFigureMenu.anchorPosition}
          sourceTitle={
            version.sources.find((source) => source.key === shownFigureMenu.target.sourceKey)?.title ??
            shownFigureMenu.target.sourceKey
          }
          target={shownFigureMenu.target}
          onOpenReport={handleOpenReport}
          onShowLedgerEntries={handleShowLedgerEntries}
          onClose={() => setFigureMenu(undefined)}
        />
      )}
      {isDrillMode && !isReportWindowOpen && (
        <ArtifactLedgerDrillDown ref={ledgerRef} artifactId={artifact.id} onReload={handleRefreshClick} />
      )}
      <Outlet />
    </Stack>
  );
};

const ArtifactPage = ({ mode }: Props) => {
  const { id = "" } = useParams();
  return <ArtifactPageContent key={id} artifactId={id} mode={mode} />;
};

export default ArtifactPage;
